import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  FileText,
  Loader2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { createLead } from "../lib/api";

/**
 * Panneau réutilisable de demande de devis rapide (4 champs).
 *
 * Utilisé par :
 * - Configurator (visualisation IA de façade)
 * - Diagnosis (envoi du rapport diagnostic IA)
 * - Diagnosis (diagnostic IA de la maison)
 *
 * Props :
 * @param {string} ctaLabel     — libellé du bouton primaire (état 1)
 * @param {string} ctaEyebrow   — micro-titre orange au-dessus du CTA
 * @param {string} ctaTitle     — titre principal (état 1)
 * @param {string} ctaSubtitle  — sous-titre explicatif (état 1)
 * @param {string} formTitle    — titre du formulaire (état 2)
 * @param {string} formSubtitle — sous-titre du formulaire (état 2)
 * @param {string} formHelper   — petite phrase sous le formulaire précisant
 *                                ce qui sera joint automatiquement.
 * @param {string} successTitle — titre du message de succès (état 3)
 * @param {string} successBody  — corps du message de succès (état 3)
 * @param {() => object|Promise<object>} buildPayload
 *                              — fonction qui retourne le payload complet
 *                                envoyé à /api/leads (photos, options, données
 *                                IA, contexte spécifique à chaque outil).
 * @param {string=} testIdPrefix — préfixe des data-testid.
 * @param {ReactNode=} secondaryAction — bouton secondaire optionnel à côté
 *                                       du CTA principal (état 1 uniquement).
 */
export default function QuickLeadPanel({
  ctaLabel = "Envoyer ma demande",
  ctaEyebrow = "Rappel gratuit sous 24 h",
  ctaTitle = "Prêt à recevoir votre devis ?",
  ctaSubtitle = "GMC vous rappelle rapidement.",
  formTitle = "Vos coordonnées",
  formSubtitle = "GMC Rénovation Façade vous rappelle rapidement pour votre devis gratuit.",
  formHelper,
  successTitle = "Votre demande a bien été envoyée",
  successBody = "GMC Rénovation Façade vous rappelle rapidement pour votre devis gratuit.",
  buildPayload,
  testIdPrefix = "quick-lead",
  secondaryAction = null,
}) {
  const [showForm, setShowForm] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    prenom: "",
    nom: "",
    phone: "",
    email: "",
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    const { prenom, nom, phone, email } = form;
    if (!prenom.trim() || !nom.trim() || !phone.trim() || !email.trim()) {
      toast.error("Merci de renseigner les 4 champs.");
      return;
    }
    setSubmitting(true);
    try {
      const extra = (await buildPayload?.()) || {};
      await createLead({
        name: `${prenom.trim()} ${nom.trim()}`,
        phone: phone.trim(),
        email: email.trim(),
        city: extra.city || "Non renseignée",
        ...extra,
      });
      setSuccess(true);
    } catch (err) {
      toast.error(
        "Envoi impossible. Réessayez ou appelez-nous au 07 63 43 38 67."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      key={success ? "success" : showForm ? "form" : "cta"}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid={`${testIdPrefix}-panel`}
      className="relative overflow-hidden rounded-3xl border border-[#FF5A00]/40 bg-gradient-to-br from-[#FF5A00]/10 via-[#0a0a0a] to-[#0a0a0a] p-6 md:p-8"
    >
      <div
        aria-hidden
        className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#FF5A00]/15 blur-3xl pointer-events-none"
      />

      {!showForm && !success && (
        <div className="relative flex flex-col items-center text-center">
          <div className="eyebrow mb-3 inline-flex items-center gap-2">
            <Sparkles size={12} />
            <span>{ctaEyebrow}</span>
          </div>
          <h3 className="font-display text-2xl md:text-3xl text-white mb-2 leading-tight">
            {ctaTitle}
          </h3>
          <p className="text-neutral-400 text-sm md:text-base max-w-lg mb-6">
            {ctaSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-center">
            <button
              type="button"
              onClick={() => setShowForm(true)}
              data-testid={`${testIdPrefix}-cta`}
              className="btn-primary justify-center text-base px-8 py-4"
            >
              <FileText size={18} /> {ctaLabel} <ArrowRight size={16} />
            </button>
            {secondaryAction}
          </div>
        </div>
      )}

      {showForm && !success && (
        <form
          onSubmit={onSubmit}
          className="relative"
          data-testid={`${testIdPrefix}-form`}
        >
          <div className="mb-6">
            <h3 className="font-display text-2xl md:text-3xl text-white mb-1.5 leading-tight">
              {formTitle}
            </h3>
            <p className="text-neutral-400 text-sm">{formSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
            <Field
              label="Prénom"
              value={form.prenom}
              onChange={(v) => setForm((f) => ({ ...f, prenom: v }))}
              testId={`${testIdPrefix}-prenom`}
              autoComplete="given-name"
              required
            />
            <Field
              label="Nom"
              value={form.nom}
              onChange={(v) => setForm((f) => ({ ...f, nom: v }))}
              testId={`${testIdPrefix}-nom`}
              autoComplete="family-name"
              required
            />
            <Field
              label="Téléphone"
              type="tel"
              value={form.phone}
              onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
              testId={`${testIdPrefix}-phone`}
              autoComplete="tel"
              required
            />
            <Field
              label="Adresse mail"
              type="email"
              value={form.email}
              onChange={(v) => setForm((f) => ({ ...f, email: v }))}
              testId={`${testIdPrefix}-email`}
              autoComplete="email"
              required
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 items-center">
            <button
              type="submit"
              disabled={submitting}
              data-testid={`${testIdPrefix}-submit`}
              className="btn-primary justify-center text-base px-8 py-4 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Envoi…
                </>
              ) : (
                <>
                  <Check size={16} /> Envoyer
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              disabled={submitting}
              data-testid={`${testIdPrefix}-cancel`}
              className="text-neutral-400 text-sm hover:text-white transition-colors underline underline-offset-4"
            >
              Annuler
            </button>
          </div>

          {formHelper && (
            <p className="text-[11px] text-neutral-500 mt-5 leading-relaxed">
              {formHelper}
            </p>
          )}
        </form>
      )}

      {success && (
        <div
          className="relative text-center py-4"
          data-testid={`${testIdPrefix}-success`}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF5A00]/15 mb-5">
            <CheckCircle2 size={32} className="text-[#FF5A00]" />
          </div>
          <h3 className="font-display text-2xl md:text-3xl text-white mb-3 leading-tight">
            {successTitle}
          </h3>
          <p className="text-neutral-300 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            {successBody}
          </p>
          <p className="text-sm text-neutral-500 mt-4">
            Une urgence ? Appelez-nous directement au{" "}
            <a
              href="tel:+33763433867"
              className="text-[#FF5A00] hover:underline"
            >
              07 63 43 38 67
            </a>
            .
          </p>
        </div>
      )}
    </motion.div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  testId,
  autoComplete,
  required,
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.14em] text-neutral-400 mb-1.5">
        {label}
        {required && <span className="text-[#FF5A00] ml-1">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        autoComplete={autoComplete}
        data-testid={testId}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A00]/60 focus:ring-2 focus:ring-[#FF5A00]/20 transition"
      />
    </label>
  );
}
